import React, { useEffect, useState } from 'react';
import { OverViewRole } from './DRCMonitorList';
import { COMMON_FILTER_TEXT } from '../../lang';
import {
  currentStatus,
  hkpfsIndicator,
  localIndicator,
  programme,
} from '../../GSAssessment';
import {
  type DropDownSelectOption,
  type OptionGroups,
  FilterPanel,
  type FilterPanelProps,
  Loading,
  Alert,
} from '../../../../components';
import { useGetChoiceData } from '../../../Application/graphql';
import { useAuth } from '../../../../contexts';
import { convertEntryYearMap } from '../../../Application/components';

type DSRCFilterFilterProps = Omit<FilterPanelProps, 'filterOptionGroups'> & {
  apiType: DSRC_FILTER_API_TYPE;
  overViewRole?: OverViewRole;
};

export function getFilterPriority(type: 'select' | 'text'): number {
  switch (type) {
    case 'select':
      return -1;
    case 'text':
      return 1;
    default:
      return 0;
  }
}

export const enum DSRC_FILTER_API_TYPE {
  nominationsPaginated = 'nominationsPaginated',
  applicationsPaginated = 'applicationsPaginated',
  interviewPanelApplicationList = 'interviewPanelApplicationList',
}

const enum CustomizedField {
  entryYear = 'entryYear',
  status = 'status',
  programme = 'programme',
  isLocal = 'isLocal',
  applicationNo = 'applicationNo',
  email = 'email',
}

const mappings: Record<
  DSRC_FILTER_API_TYPE,
  Record<CustomizedField, string>
> = {
  [DSRC_FILTER_API_TYPE.nominationsPaginated]: {
    [CustomizedField.entryYear]: 'application.programmeChoice.entryYear',
    [CustomizedField.status]: 'application.status',
    [CustomizedField.programme]: 'application.programmeChoice.programme',
    [CustomizedField.isLocal]: 'application.applicant.identity.isLocal',
    [CustomizedField.applicationNo]: 'application.applicationNo',
    [CustomizedField.email]: 'application.applicant.email',
  },
  [DSRC_FILTER_API_TYPE.applicationsPaginated]: {
    [CustomizedField.entryYear]: 'programmeChoice.entryYear',
    [CustomizedField.status]: 'status',
    [CustomizedField.programme]: 'programmeChoice.programme',
    [CustomizedField.isLocal]: 'applicant.identity.isLocal',
    [CustomizedField.applicationNo]: 'applicationNo',
    [CustomizedField.email]: 'applicant.email',
  },
  [DSRC_FILTER_API_TYPE.interviewPanelApplicationList]: {
    [CustomizedField.entryYear]: 'programmeChoice.entryYear',
    [CustomizedField.status]: 'status',
    [CustomizedField.programme]: 'programmeChoice.programme',
    [CustomizedField.isLocal]: 'applicant.identity.isLocal',
    [CustomizedField.applicationNo]: 'applicationNo',
    [CustomizedField.email]: 'applicant.email',
  },
};

// used for FRC and DRC and GS
export const DSRCFilter: React.FC<DSRCFilterFilterProps> = props => {
  const {
    initialAppliedFilter,
    apiType,
    setFilter: setParentFilter,
    overViewRole,
  } = props;
  const { user } = useAuth();
  const { userInfo } = user ?? {};
  const { rightFRCDepartmentChairList = [] } = userInfo ?? {};
  const [viewRole, setViewRole] = useState<OverViewRole | undefined>(
    overViewRole,
  );
  const {
    programmeChoiceMetadata: departmentsAndEntryYear,
    loading,
    error,
  } = useGetChoiceData();

  const { entryYears, departments } = departmentsAndEntryYear;

  const entryYearsOptions: DropDownSelectOption[] = (entryYears as any[]).map(
    entryYear => ({
      value: entryYear,
      label: convertEntryYearMap[entryYear] ?? entryYear,
    }),
  );
  const frcDepartmentOptions: DropDownSelectOption[] = (
    rightFRCDepartmentChairList as any[]
  ).map(FRCDepartment => ({
    value: FRCDepartment,
    label:
      departments.find(dept => dept.value === FRCDepartment)?.label ?? 'NA',
  }));

  const gsDepartmentOptions: DropDownSelectOption[] = departments.map(
    department => ({
      value: department.value,
      label: department.label,
    }),
  );

  useEffect(() => {
    setViewRole(overViewRole);
  }, [overViewRole]);

  // ENHANCE!! configuration is required
  const needDept =
    [OverViewRole.FRC, OverViewRole.GS, OverViewRole.INTERVIEW_PANEL].some(
      role => role === viewRole,
    ) &&
    [
      DSRC_FILTER_API_TYPE.applicationsPaginated,
      DSRC_FILTER_API_TYPE.nominationsPaginated,
      DSRC_FILTER_API_TYPE.interviewPanelApplicationList,
    ].includes(apiType);

  const deptQueryPathMap: Partial<Record<OverViewRole, string>> = {
    [OverViewRole.GS]: 'application.programmeChoice.department',
    [OverViewRole.INTERVIEW_PANEL]: 'programmeChoice.department',
  };
  const deptOptionGroup: OptionGroups = {
    title: COMMON_FILTER_TEXT.FILTER_CATEGORY.DEPT,
    options:
      !(viewRole == null) &&
      [OverViewRole.GS, OverViewRole.INTERVIEW_PANEL].includes(viewRole)
        ? gsDepartmentOptions
        : frcDepartmentOptions,
    type: 'select',
    fieldName:
      deptQueryPathMap[viewRole as OverViewRole] ??
      'programmeChoice.department',
    fieldType: 'string',
    displayLabel: false,
  };

  const viewAllStatus = viewRole === OverViewRole.GS;
  const hideStatus = OverViewRole.INTERVIEW_PANEL === viewRole;
  const filterOptionGroups: OptionGroups[] = [
    ...(needDept ? [deptOptionGroup] : []),
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ENTRY_YR,
      options: entryYearsOptions,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.entryYear],
      fieldType: 'string',
      displayLabel: false,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.ADM_PROG,
      options: programme,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.programme],
      fieldType: 'string',
      displayLabel: true,
    },
    ...(apiType === DSRC_FILTER_API_TYPE.applicationsPaginated
      ? [
          {
            title: COMMON_FILTER_TEXT.FILTER_CATEGORY.HKPFS_INDICATOR,
            options: hkpfsIndicator,
            type: 'select' as OptionGroups['type'],
            fieldName: 'hkpfsInformation.HKPFSReferenceNumber',
            fieldType: 'object',
            displayLabel: true,
          },
        ]
      : []),

    ...(hideStatus
      ? []
      : [
          {
            title: COMMON_FILTER_TEXT.FILTER_CATEGORY.CURRENT_STATUS,
            options: currentStatus(!viewAllStatus),
            type: 'select' as OptionGroups['type'],
            fieldName: mappings[apiType][CustomizedField.status],
            fieldType: 'string',
            displayLabel: true,
          },
        ]),
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.EMAIL,
      fieldName: mappings[apiType][CustomizedField.email],
      type: 'text' as OptionGroups['type'],
      isFuzzy: true,
    },

    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.LOCAL_INDICATOR,
      options: localIndicator,
      type: 'select',
      fieldName: mappings[apiType][CustomizedField.isLocal],
      fieldType: 'boolean',
      displayLabel: true,
    },
    {
      title: COMMON_FILTER_TEXT.FILTER_CATEGORY.APPLICATION_NO,
      fieldName: mappings[apiType][CustomizedField.applicationNo],
      type: 'text',
      isFuzzy: true,
    },
  ];

  if (error) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent: `Error Occurs.` + (error as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  filterOptionGroups.sort((a: OptionGroups, b: OptionGroups) => {
    return getFilterPriority(a.type) - getFilterPriority(b.type);
  });
  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <FilterPanel
          setFilter={setParentFilter}
          initialAppliedFilter={initialAppliedFilter}
          filterOptionGroups={filterOptionGroups}
        />
      )}
    </>
  );
};
